import {
  Grid,
  MenuItem,
  Select,
  SelectChangeEvent,
  Stack,
  TextField,
  Typography,
} from "@mui/material"
import { Mode } from "../../components/ReadWriteTextField"
import { useEffect, useState } from "react"
import { SectionHead } from "./SectionHead"
import { Estate } from "../../models/Estate"
import { useAuthContext } from "AuthContextWrapper"
import { LocalizationProvider, DesktopDatePicker } from "@mui/lab"
import AdapterDateFns from "@mui/lab/AdapterDateFns"
import { NO_OPTION, YES_OPTION } from "components/ReadWriteOptionGroup"
import { useProbateContext } from "ProbateContextWrapper"
import { PROBATE_IDS } from "../../constants"
import { ReadWriteWrapper } from "components/ReadWriteWrapper"
import { isValidDate } from "../../utils/isValidDate"

interface IProps {
  allowedModes?: Mode[]
  startMode?: Mode
}

export function DeceasedInfo(props: IProps): JSX.Element {
  const { activeEstate, setActiveEstate } = useAuthContext()
  const { getFieldAnswerForUser, storeFieldAnswered } = useProbateContext()
  const [mode, setMode] = useState<Mode>(props.startMode || "read")
  const [error, setError] = useState<string>("")
  const [savedSuccessfully, setSavedSuccessfully] = useState<boolean | null>(null)
  const [estateAtEditStart, setEstateAtEditStart] = useState<Estate | undefined>()
  const [hasMinorHeirsAtEditStart, setHasMinorHeirsAtEditStart] = useState<boolean | undefined>()
  const [hasMinorHeirs, setHasMinorHeirs] = useState<boolean | undefined>()
  const [myEstate, setMyEstate] = useState<Estate>()
  const [passingDateError, setPassingDateError] = useState<string>("")
  const [wasVeteranError, setWasVeteranError] = useState<string>("")
  const [hasMinorHeirsError, setHasMinorHeirsError] = useState<string>("")
  const { user } = useAuthContext()

  useEffect(() => {
    async function getKnowledgeFieldAnswers() {
      if (!user?.id) {
        return
      }
      const answerValue = await getFieldAnswerForUser(user.id, PROBATE_IDS.someHeirsAreMinors)
      setHasMinorHeirs(!!answerValue)
    }

    getKnowledgeFieldAnswers()
  }, [])

  useEffect(() => {
    if (!activeEstate) {
      return
    }

    const activeEstate2 = activeEstate
    activeEstate2.wasVeteran = activeEstate2.wasVeteran || "No"
    setMyEstate(activeEstate)
  }, [activeEstate?.id])

  function canEdit(): boolean {
    if (!props.allowedModes) {
      return true
    }

    return !!props.allowedModes?.find((m) => m === "edit")
  }

  function validate(): boolean {
    if (!myEstate) {
      return false
    }

    let hasError = false
    if (passingDateError != "" || !isValidDate(myEstate.passedOn || "")) {
      setPassingDateError("invalid date")
      hasError = true
    }

    if (myEstate.wasVeteran != "Yes" && myEstate.wasVeteran != "No") {
      setWasVeteranError("Please select a value")
      hasError = true
    }

    if (hasMinorHeirs === undefined) {
      setHasMinorHeirsError("Please select a value")
      hasError = true
    }

    return !hasError
  }

  async function save(userId: number) {
    if (!myEstate) {
      console.warn("no estate yet")
      return
    }

    if (!validate()) {
      console.warn("form errors, not saving")
      return
    }

    try {
      setError("")
      setSavedSuccessfully(null)

      await storeFieldAnswered({
        id: PROBATE_IDS.someHeirsAreMinors,
        answer: hasMinorHeirs ? "Yes" : "No",
        answerValue: hasMinorHeirs,
        prefix: "",
        getOptionLabel: () => undefined,
      })

      // Save estate
      const savedEstate = await myEstate.saveEstate(userId)
      if (!savedEstate?.id) {
        throw new Error("unknown error")
      }

      setActiveEstate(savedEstate)
      setMyEstate(savedEstate)
      setEstateAtEditStart(savedEstate)
      setHasMinorHeirsAtEditStart(hasMinorHeirs === undefined ? undefined : !!hasMinorHeirs)
      setSavedSuccessfully(true)
      setMode("read")
    } catch (e) {
      setError("unable to save deceased info: " + (e as any)?.message)
    }
  }

  return (
    <div>
      <SectionHead
        mode={mode}
        canEdit={canEdit()}
        header={"Deceased Info"}
        message={
          savedSuccessfully ? (
            <Typography variant="body1" color="primary">
              Saved successfully
            </Typography>
          ) : error ? (
            <Typography variant="body1" color="error">
              {error}
            </Typography>
          ) : (
            <></>
          )
        }
        onCancel={() => {
          setPassingDateError("")
          setMode("read")
          setMyEstate(estateAtEditStart)
          setHasMinorHeirs(hasMinorHeirsAtEditStart)
          setSavedSuccessfully(null)
        }}
        onEdit={() => {
          if (!myEstate) {
            setError("not yet done loading estate")
            return
          }
          const clone = Estate.fromJSON(myEstate.toJson())
          clone.id = myEstate.id
          setEstateAtEditStart(clone)
          setHasMinorHeirsAtEditStart(hasMinorHeirs === undefined ? undefined : !!hasMinorHeirs)
          setSavedSuccessfully(null)
          setMode("edit")
        }}
        onSave={() => save(user?.id || 0)}
      />

      <Grid container>
        <Grid item xs={12} sm={4} md={4} lg={4} xl={4} style={{ paddingRight: "1rem" }}>
          <Typography
            variant="body1"
            component="div"
            style={{ marginBottom: "0.5rem", fontWeight: "bold" }}
          >
            First Name
          </Typography>
          <ReadWriteWrapper
            value={myEstate?.deceasedFirstName || ""}
            mode={mode}
            blankReadOnlyValue="--"
          >
            <TextField
              size="small"
              fullWidth
              value={myEstate?.deceasedFirstName || ""}
              onChange={(e) => {
                if (!myEstate) {
                  return
                }

                const clone = Estate.fromJSON(myEstate.toJson())
                clone.id = myEstate.id
                clone.deceasedFirstName = e.target.value
                if (clone.deceasedFirstName == "") {
                  clone.deceasedFirstName = null
                }
                setMyEstate(clone)
              }}
            />
          </ReadWriteWrapper>
        </Grid>
        <Grid item xs={12} sm={3} md={3} lg={3} xl={3} style={{ paddingRight: "1rem" }}>
          <Typography
            variant="body1"
            component="div"
            style={{ marginBottom: "0.5rem", fontWeight: "bold" }}
          >
            Middle Name
          </Typography>
          <ReadWriteWrapper
            value={myEstate?.deceasedMiddleName || ""}
            mode={mode}
            blankReadOnlyValue="--"
          >
            <TextField
              value={myEstate?.deceasedMiddleName || ""}
              size="small"
              fullWidth
              onChange={(e) => {
                if (!myEstate) {
                  return
                }

                const clone = Estate.fromJSON(myEstate.toJson())
                clone.id = myEstate.id
                clone.deceasedMiddleName = e.target.value
                if (clone.deceasedMiddleName == "") {
                  clone.deceasedMiddleName = null
                }
                setMyEstate(clone)
              }}
            />
          </ReadWriteWrapper>
        </Grid>
        <Grid item xs={12} sm={4} md={4} lg={4} xl={4} style={{ paddingRight: "1rem" }}>
          <Typography
            variant="body1"
            component="div"
            style={{ marginBottom: "0.5rem", fontWeight: "bold" }}
          >
            Last Name
          </Typography>
          <ReadWriteWrapper
            value={myEstate?.deceasedLastName || ""}
            mode={mode}
            blankReadOnlyValue="--"
          >
            <TextField
              value={myEstate?.deceasedLastName || ""}
              size="small"
              fullWidth
              onChange={(e) => {
                if (!myEstate) {
                  return
                }

                const clone = Estate.fromJSON(myEstate.toJson())
                clone.id = myEstate.id
                clone.deceasedLastName = e.target.value
                if (clone.deceasedLastName == "") {
                  clone.deceasedLastName = null
                }
                setMyEstate(clone)
              }}
            />
          </ReadWriteWrapper>
        </Grid>
      </Grid>

      <Grid container style={{ marginTop: "1rem" }}>
        <Grid item xs={12} sm={4} md={4} lg={4} xl={4} style={{ paddingRight: "1rem" }}>
          <Typography
            variant="body1"
            component="div"
            style={{ marginBottom: "0.5rem", fontWeight: "bold" }}
          >
            Date of death
          </Typography>
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Stack spacing={3}>
              <DesktopDatePicker
                inputFormat="MM/dd/yyyy"
                minDate={new Date("1900-01-01")}
                maxDate={new Date()}
                value={myEstate?.passedOn}
                readOnly={mode !== "edit"}
                onChange={(date: Date | null) => {
                  if (!myEstate) {
                    return
                  }

                  const clone = Estate.fromJSON(myEstate.toJson())
                  clone.id = myEstate.id
                  try {
                    if (date) {
                      date.setHours(20) // avoid rolling back 1 day on selection
                      clone.passedOn = date.toISOString().substring(0, 10)
                      if (!isValidDate(clone.passedOn)) {
                        throw new Error("invalid date")
                      }

                      setPassingDateError("")
                    } else {
                      clone.passedOn = null
                      setPassingDateError("")
                    }
                  } catch (e) {
                    clone.passedOn = null
                    setPassingDateError("invalid date")
                  }
                  setMyEstate(clone)
                  return true
                }}
                renderInput={(params) => {
                  return (
                    <ReadWriteWrapper
                      value={
                        myEstate?.passedOn ? new Date(myEstate.passedOn).toLocaleDateString() : ""
                      }
                      mode={mode}
                      blankReadOnlyValue="--"
                    >
                      <TextField
                        error={!!passingDateError}
                        value={myEstate?.passedOn || ""}
                        style={{
                          outlineColor: "red !important",
                          borderColor: "red !important",
                          outlineWidth: "2",
                        }}
                        size="small"
                        fullWidth
                        {...params}
                      />
                    </ReadWriteWrapper>
                  )
                }}
              />
            </Stack>
          </LocalizationProvider>
          {passingDateError && (
            <Typography component="div" color="error">
              {passingDateError}
            </Typography>
          )}
        </Grid>
      </Grid>
      <Grid container style={{ marginTop: "1rem" }}>
        <Grid item xs={12} sm={4} md={4} lg={4} xl={4}>
          <Typography
            variant="body1"
            component="div"
            style={{ marginBottom: "0.5rem", fontWeight: "bold" }}
          >
            Veteran status
          </Typography>
          <ReadWriteWrapper
            value={myEstate?.wasVeteran == "Yes" ? YES_OPTION.label : NO_OPTION.label}
            mode={mode}
            blankReadOnlyValue="--"
          >
            <Select
              size="small"
              readOnly={mode !== "edit"}
              onChange={(e: SelectChangeEvent) => {
                if (!myEstate) {
                  return
                }
                setWasVeteranError("")

                const clone = Estate.fromJSON(myEstate.toJson())
                clone.id = myEstate.id
                clone.wasVeteran = e.target.value == YES_OPTION.value ? "Yes" : "No"
                setMyEstate(clone)
                return true
              }}
              value={
                myEstate?.wasVeteran === "Yes"
                  ? YES_OPTION.value.toString()
                  : NO_OPTION.value.toString()
              }
            >
              {[YES_OPTION, NO_OPTION].map((option, index) => {
                return (
                  <MenuItem key={"option" + index} value={option.value.toString()}>
                    {option.label}{" "}
                  </MenuItem>
                )
              })}
            </Select>
          </ReadWriteWrapper>
          {wasVeteranError && (
            <Typography color="error" component={"div"}>
              {wasVeteranError}
            </Typography>
          )}
        </Grid>
      </Grid>
      <Grid container style={{ marginTop: "1rem" }}>
        <Grid item xs={12} sm={4} md={4} lg={4} xl={4}>
          <Typography
            variant="body1"
            component="div"
            style={{ marginBottom: "0.5rem", fontWeight: "bold" }}
          >
            Minor heirs
          </Typography>
          <ReadWriteWrapper
            value={hasMinorHeirs ? YES_OPTION.label : NO_OPTION.label}
            mode={mode}
            blankReadOnlyValue="--"
          >
            <Select
              size="small"
              readOnly={mode !== "edit"}
              onChange={(e: SelectChangeEvent) => {
                setHasMinorHeirs(e.target.value == YES_OPTION.value)
                return true
              }}
              value={hasMinorHeirs ? YES_OPTION.value.toString() : NO_OPTION.value.toString()}
            >
              {[YES_OPTION, NO_OPTION].map((option, index) => {
                return (
                  <MenuItem key={"option" + index} value={option.value.toString()}>
                    {option.label}{" "}
                  </MenuItem>
                )
              })}
            </Select>
          </ReadWriteWrapper>
          {hasMinorHeirsError && (
            <Typography color="error" component={"div"}>
              {hasMinorHeirsError}
            </Typography>
          )}
        </Grid>
      </Grid>
    </div>
  )
}
